import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ==================== Dashboard History Log API Functions ====================

/**
 * Get all dashboard history logs
 * @param {number} dashboardId - Optional dashboard ID to filter by specific dashboard
 * @returns {Promise} Array of history logs
 */
export const getDashboardHistoryLogs = async (dashboardId = null) => {
    try {
        const url = dashboardId ? `/dashboard-history-logs/?dashboard_id=${dashboardId}` : '/dashboard-history-logs/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching dashboard history logs:', error);
        throw error;
    }
};

/**
 * Get a specific dashboard history log by ID
 * @param {number} logId - History log ID
 * @returns {Promise} History log object
 */
export const getDashboardHistoryLogDetail = async (logId) => {
    try {
        const response = await api.get(`/dashboard-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching dashboard history log detail:', error);
        throw error;
    }
};

/**
 * Create a new dashboard history log entry
 * @param {object} logData - Log data including dashboard, user, log_title, log_date, log_data
 * @returns {Promise} Created log object
 */
export const createDashboardHistoryLog = async (logData) => {
    try {
        const response = await api.post('/dashboard-history-logs/', logData);
        return response.data;
    } catch (error) {
        console.error('Error creating dashboard history log:', error);
        throw error;
    }
};

/**
 * Update an existing dashboard history log
 * @param {number} logId - History log ID
 * @param {object} logData - Updated log data
 * @returns {Promise} Updated log object
 */
export const updateDashboardHistoryLog = async (logId, logData) => {
    try {
        const response = await api.patch(`/dashboard-history-logs/${logId}/`, logData);
        return response.data;
    } catch (error) {
        console.error('Error updating dashboard history log:', error);
        throw error;
    }
};

/**
 * Delete a dashboard history log
 * @param {number} logId - History log ID
 * @returns {Promise} Deletion response
 */
export const deleteDashboardHistoryLog = async (logId) => {
    try {
        const response = await api.delete(`/dashboard-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error deleting dashboard history log:', error);
        throw error;
    }
};

// =================== Level Page History Log API Functions ====================

/**
 * Get all level page history logs
 * @param {number} levelId - Optional level ID to filter by specific level
 * @returns {Promise} Array of history logs
 */
export const getLevelHistoryLogs = async (levelId = null) => {
    try {
        const url = levelId ? `/level-history-logs/?level_id=${levelId}` : '/level-history-logs/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching level history logs:', error);
        throw error;
    }
};

/**
 * Get a specific level page history log by ID
 * @param {number} logId - History log ID
 * @returns {Promise} History log object
 */
export const getLevelHistoryLogDetail = async (logId) => {
    try {
        const response = await api.get(`/level-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching level history log detail:', error);
        throw error;
    }
};

/**
 * Create a new level page history log entry
 * @param {object} logData - Log data including level, user, log_title, log_date, log_data
 * @returns {Promise} Created log object
 */
export const createLevelHistoryLog = async (logData) => {
    try {
        const response = await api.post('/level-history-logs/', logData);
        return response.data;
    } catch (error) {
        console.error('Error creating level history log:', error);
        throw error;
    }
};

/**
 * Update an existing level page history log
 * @param {number} logId - History log ID
 * @param {object} logData - Updated log data
 * @returns {Promise} Updated log object
 */
export const updateLevelHistoryLog = async (logId, logData) => {
    try {
        const response = await api.patch(`/level-history-logs/${logId}/`, logData);
        return response.data;
    } catch (error) {
        console.error('Error updating level history log:', error);
        throw error;
    }
};

/**
 * Delete a level page history log
 * @param {number} logId - History log ID
 * @returns {Promise} Deletion response
 */
export const deleteLevelHistoryLog = async (logId) => {
    try {
        const response = await api.delete(`/level-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error deleting level history log:', error);
        throw error;
    }
};

// ================== Exam History Log API Functions ====================

/**
 * Get all exam history logs
 * @param {number} examId - Optional exam ID to filter by specific exam
 * @returns {Promise} Array of history logs
 */
export const getExamHistoryLogs = async (examId = null) => {
    try {
        const url = examId ? `/exam-history-logs/?exam_id=${examId}` : '/exam-history-logs/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching exam history logs:', error);
        throw error;
    }
};

/**
 * Get a specific exam history log by ID
 * @param {number} logId - History log ID
 * @returns {Promise} History log object
 */
export const getExamHistoryLogDetail = async (logId) => {
    try {
        const response = await api.get(`/exam-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching exam history log detail:', error);
        throw error;
    }
};

/**
 * Create a new exam history log entry
 * @param {object} logData - Log data including exam, user, log_title, log_date, log_data
 * @returns {Promise} Created log object
 */
export const createExamHistoryLog = async (logData) => {
    try {
        const response = await api.post('/exam-history-logs/', logData);
        return response.data;
    } catch (error) {
        console.error('Error creating exam history log:', error);
        throw error;
    }
};

/**
 * Update an existing exam history log
 * @param {number} logId - History log ID
 * @param {object} logData - Updated log data
 * @returns {Promise} Updated log object
 */
export const updateExamHistoryLog = async (logId, logData) => {
    try {
        const response = await api.patch(`/exam-history-logs/${logId}/`, logData);
        return response.data;
    } catch (error) {
        console.error('Error updating exam history log:', error);
        throw error;
    }
};

/**
 * Delete an exam history log
 * @param {number} logId - History log ID
 * @returns {Promise} Deletion response
 */
export const deleteExamHistoryLog = async (logId) => {
    try {
        const response = await api.delete(`/exam-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error deleting exam history log:', error);
        throw error;
    }
};

// ================== Test History Log API Functions ====================

/**
 * Get all test history logs
 * @param {number} testId - Optional test ID to filter by specific test
 * @returns {Promise} Array of history logs
 */
export const getTestHistoryLogs = async (testId = null) => {
    try {
        const url = testId ? `/test-history-logs/?test_id=${testId}` : '/test-history-logs/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching test history logs:', error);
        throw error;
    }
};

/**
 * Get a specific test history log by ID
 * @param {number} logId - History log ID
 * @returns {Promise} History log object
 */
export const getTestHistoryLogDetail = async (logId) => {
    try {
        const response = await api.get(`/test-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching test history log detail:', error);
        throw error;
    }
};

/**
 * Create a new test history log entry
 * @param {object} logData - Log data including test, user, log_title, log_date, log_data
 * @returns {Promise} Created log object
 */
export const createTestHistoryLog = async (logData) => {
    try {
        const response = await api.post('/test-history-logs/', logData);
        return response.data;
    } catch (error) {
        console.error('Error creating test history log:', error);
        throw error;
    }
};

/**
 * Update an existing test history log
 * @param {number} logId - History log ID
 * @param {object} logData - Updated log data
 * @returns {Promise} Updated log object
 */
export const updateTestHistoryLog = async (logId, logData) => {
    try {
        const response = await api.patch(`/test-history-logs/${logId}/`, logData);
        return response.data;
    } catch (error) {
        console.error('Error updating test history log:', error);
        throw error;
    }
};

/**
 * Delete a test history log
 * @param {number} logId - History log ID
 * @returns {Promise} Deletion response
 */
export const deleteTestHistoryLog = async (logId) => {
    try {
        const response = await api.delete(`/test-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error deleting test history log:', error);
        throw error;
    }
};

// ==================== CHAPTER HISTORY LOG API FUNCTIONS ====================

/**
 * Get all chapter history logs
 * @param {number} chapterId - Optional chapter ID to filter by specific chapter
 * @returns {Promise} Array of history logs
 */
export const getChapterHistoryLogs = async (chapterId = null) => {
    try {
        const url = chapterId ? `/chapters/${chapterId}/history-logs/` : '/chapter-history-logs/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching chapter history logs:', error);
        throw error;
    }
};

/**
 * Get a specific history log by ID
 * @param {number} logId - History log ID
 * @returns {Promise} History log object
 */
export const getChapterHistoryLogDetail = async (logId) => {
    try {
        const response = await api.get(`/chapter-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching chapter history log detail:', error);
        throw error;
    }
};

/**
 * Create a new chapter history log entry
 * @param {object} logData - Log data including chapter, user, log_title, log_date, log_data
 * @returns {Promise} Created log object
 */
export const createChapterHistoryLog = async (logData) => {
    try {
        const response = await api.post('/chapter-history-logs/', logData);
        return response.data;
    } catch (error) {
        console.error('Error creating chapter history log:', error);
        throw error;
    }
};

/**
 * Update an existing chapter history log
 * @param {number} logId - History log ID
 * @param {object} logData - Updated log data
 * @returns {Promise} Updated log object
 */
export const updateChapterHistoryLog = async (logId, logData) => {
    try {
        const response = await api.patch(`/chapter-history-logs/${logId}/`, logData);
        return response.data;
    } catch (error) {
        console.error('Error updating chapter history log:', error);
        throw error;
    }
};

/**
 * Delete a chapter history log
 * @param {number} logId - History log ID
 * @returns {Promise} Deletion response
 */
export const deleteChapterHistoryLog = async (logId) => {
    try {
        const response = await api.delete(`/chapter-history-logs/${logId}/`);
        return response.data;
    } catch (error) {
        console.error('Error deleting chapter history log:', error);
        throw error;
    }
};

/**
 * Delete all chapter history logs for a chapter
 * @param {number} chapterId - Chapter ID
 * @returns {Promise} Deletion response
 */
export const deleteAllChapterHistoryLogs = async (chapterId) => {
    try {
        const response = await api.delete(`/chapters/${chapterId}/history-logs/delete-all/`);
        return response.data;
    } catch (error) {
        console.error('Error deleting all chapter history logs:', error);
        throw error;
    }
};
